import React from "react";
import { 
  FileText, 
  FileSpreadsheet, 
  FileImage, 
  FileVideo, 
  FileArchive, 
  File, 
  Download, 
  MoreVertical, 
  Trash2, 
  Info,
  Calendar,
  Tag
} from "lucide-react";

export default function DocumentCard({
  document: docItem,
  viewMode = "grid",
  folderName,
  onClick,
  onDownload,
  onDelete
}) {
  const getFileIconAndColor = (type, ext) => {
    switch (type) {
      case "pdf":
        return { icon: FileText, color: "#EF4444", bg: "rgba(239, 68, 68, 0.12)", label: "PDF" };
      case "presentation":
        return { icon: FileText, color: "#F59E0B", bg: "rgba(245, 158, 11, 0.12)", label: ext?.toUpperCase() || "PPT" };
      case "spreadsheet":
        return { icon: FileSpreadsheet, color: "#10B981", bg: "rgba(16, 185, 129, 0.12)", label: ext?.toUpperCase() || "XLS" };
      case "document":
        return { icon: FileText, color: "#3B82F6", bg: "rgba(59, 130, 246, 0.12)", label: ext?.toUpperCase() || "DOC" };
      case "image":
        return { icon: FileImage, color: "#8B5CF6", bg: "rgba(139, 92, 246, 0.12)", label: ext?.toUpperCase() || "IMG" };
      case "video":
        return { icon: FileVideo, color: "#6366F1", bg: "rgba(99, 102, 241, 0.12)", label: ext?.toUpperCase() || "VID" };
      case "archive":
        return { icon: FileArchive, color: "#64748B", bg: "rgba(100, 116, 139, 0.12)", label: ext?.toUpperCase() || "ZIP" };
      default:
        return { icon: File, color: "#94A3B8", bg: "rgba(148, 163, 184, 0.12)", label: ext?.toUpperCase() || "FILE" };
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const { icon: FileIcon, color, bg, label } = getFileIconAndColor(docItem.fileType, docItem.extension);

  if (viewMode === "list") {
    return (
      <tr 
        className="marketing-doc-row"
        onClick={() => onClick(docItem)}
        style={{ cursor: "pointer" }}
      >
        {/* Document Title & File Name */}
        <td style={{ padding: "0.85rem 1rem", minWidth: "240px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <div 
              style={{ 
                width: "36px", 
                height: "36px", 
                borderRadius: "6px", 
                backgroundColor: bg, 
                color: color, 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center",
                flexShrink: 0
              }}
            >
              <FileIcon size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "0.9rem", lineHeight: 1.3 }}>
                {docItem.title || docItem.originalFileName}
              </div>
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.15rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {docItem.originalFileName}
              </div>
            </div>
          </div>
        </td>

        {/* Format Pill */}
        <td style={{ padding: "0.85rem 1rem", whiteSpace: "nowrap" }}>
          <span 
            style={{ 
              fontSize: "0.7rem", 
              padding: "0.2rem 0.5rem", 
              borderRadius: "4px", 
              backgroundColor: bg, 
              color: color,
              fontWeight: 700,
              letterSpacing: "0.05em"
            }}
          >
            {label}
          </span>
        </td>

        {/* File Size */}
        <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
          {formatFileSize(docItem.fileSize)}
        </td>

        {/* Folder Location */}
        <td style={{ padding: "0.85rem 1rem", whiteSpace: "nowrap" }}>
          <span 
            style={{ 
              fontSize: "0.75rem", 
              padding: "0.2rem 0.55rem", 
              borderRadius: "4px", 
              backgroundColor: "var(--bg-input)", 
              border: "1px solid var(--border-color)",
              color: "var(--text-secondary)",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.3rem"
            }}
          >
            📁 {folderName || "Root"}
          </span>
        </td>

        {/* Tags */}
        <td style={{ padding: "0.85rem 1rem" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
            {(docItem.tags && docItem.tags.length > 0) ? (
              docItem.tags.slice(0, 2).map(tag => (
                <span key={tag} className="sku-badge" style={{ fontSize: "0.68rem", padding: "0.1rem 0.4rem" }}>
                  #{tag}
                </span>
              ))
            ) : (
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", fontStyle: "italic" }}>—</span>
            )}
            {(docItem.tags || []).length > 2 && (
              <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                +{(docItem.tags || []).length - 2}
              </span>
            )}
          </div>
        </td>

        {/* Uploaded By */}
        <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
          {docItem.uploadedBy || "Team"}
        </td>

        {/* Date Uploaded */}
        <td style={{ padding: "0.85rem 1rem", fontSize: "0.8rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
          {docItem.uploadedAt ? new Date(docItem.uploadedAt).toLocaleDateString() : "N/A"}
        </td>

        {/* Downloads Count */}
        <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", color: "var(--text-secondary)", textAlign: "center", whiteSpace: "nowrap" }}>
          <span 
            style={{ 
              fontSize: "0.75rem", 
              padding: "0.15rem 0.45rem", 
              borderRadius: "10px", 
              backgroundColor: "var(--bg-input)", 
              color: "var(--text-secondary)",
              fontWeight: 600
            }}
          >
            {docItem.downloadsCount || 0}
          </span>
        </td>

        {/* Actions */}
        <td style={{ padding: "0.85rem 1rem", textAlign: "right", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
            <button
              type="button"
              className="btn btn-secondary btn-icon"
              onClick={() => onDownload(docItem)}
              title="Download file"
              style={{ width: "32px", height: "32px", padding: 0, color: "var(--primary)" }}
            >
              <Download size={14} />
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-icon"
              onClick={() => onClick(docItem)}
              title="View details"
              style={{ width: "32px", height: "32px", padding: 0 }}
            >
              <Info size={14} />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <div
      className="marketing-doc-card card glass-card"
      onClick={() => onClick(docItem)}
      style={{
        padding: "1.25rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        cursor: "pointer",
        position: "relative",
        transition: "all 0.2s ease"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.borderColor = "var(--border-focus)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = "var(--border-color)";
      }}
    >
      <div>
        {/* Top bar: File format badge & Actions */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.85rem" }}>
          <span 
            style={{ 
              fontSize: "0.7rem", 
              padding: "0.2rem 0.5rem", 
              borderRadius: "4px", 
              backgroundColor: bg, 
              color: color,
              fontWeight: 700,
              letterSpacing: "0.05em"
            }}
          >
            {label}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(docItem);
            }}
            className="btn btn-secondary btn-icon"
            title="Download file"
            style={{
              width: "30px",
              height: "30px",
              padding: 0,
              color: "var(--primary)"
            }}
          >
            <Download size={14} />
          </button>
        </div>

        {/* Center File Icon & Title */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", marginBottom: "0.85rem" }}>
          <div 
            style={{ 
              width: "44px", 
              height: "44px", 
              borderRadius: "8px", 
              backgroundColor: bg, 
              color: color, 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            <FileIcon size={22} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div 
              style={{ 
                fontWeight: 600, 
                color: "var(--text-primary)", 
                fontSize: "0.95rem",
                lineHeight: 1.3,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}
              title={docItem.title || docItem.originalFileName}
            >
              {docItem.title || docItem.originalFileName}
            </div>
            <div 
              style={{ 
                fontSize: "0.75rem", 
                color: "var(--text-muted)", 
                marginTop: "0.2rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {docItem.originalFileName}
            </div>
          </div>
        </div>

        {/* Tags */}
        {(docItem.tags || []).length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: "0.85rem" }}>
            {(docItem.tags || []).slice(0, 3).map((tag) => (
              <span key={tag} className="sku-badge" style={{ fontSize: "0.68rem", padding: "0.1rem 0.45rem" }}>
                #{tag}
              </span>
            ))}
            {(docItem.tags || []).length > 3 && (
              <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", alignSelf: "center" }}>
                +{(docItem.tags || []).length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div 
        style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          paddingTop: "0.75rem", 
          borderTop: "1px solid var(--border-color)",
          fontSize: "0.75rem",
          color: "var(--text-secondary)"
        }}
      >
        <span>{formatFileSize(docItem.fileSize)}</span>
        <span>{docItem.uploadedAt ? new Date(docItem.uploadedAt).toLocaleDateString() : "N/A"}</span>
      </div>
    </div>
  );
}
